import { KeyboardEvent, MouseEvent, TouchEvent } from "react";
import moment from "moment";
import { twMerge } from "tailwind-merge";
import {
  ContiguousTimeslot,
  Timeslot,
} from "@platform/features/timeslots/timeslots.types";
import { DAYS_FULL } from "@platform/features/timeslots/timeslots.constants";
import { END_HOUR } from "../../scheduling.constants";
import { SchedulingAvailabilityTimeSlotBadge } from "../SchedulingAvailabilityTimeSlotBadge/SchedulingAvailabilityTimeSlotBadge";

export type Props = {
  timeSlot: Timeslot;
  isSelected: boolean;
  isFirstSlotInContiguousBlock: boolean;
  matchingSlot?: ContiguousTimeslot;
  onMouseDown: (e: MouseEvent<HTMLDivElement>, timeSlot: Timeslot) => void;
  onMouseOver: (e: MouseEvent<HTMLDivElement>, timeSlot: Timeslot) => void;
  onTouchStart: (e: TouchEvent<HTMLDivElement>, timeSlot: Timeslot) => void;
  onTouchMove: (e: TouchEvent<HTMLDivElement>, timeSlot: Timeslot) => void;
  onTouchEnd: (e: TouchEvent<HTMLDivElement>, timeSlot: Timeslot) => void;
  dayIndex: number;
  timeIndex: number;
};

export function SchedulingAvailabilityTimeSlot({
  timeSlot,
  isSelected,
  isFirstSlotInContiguousBlock,
  matchingSlot,
  onMouseDown,
  onMouseOver,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  dayIndex,
  timeIndex,
}: Props) {
  const onKeyDown = (
    e: KeyboardEvent<HTMLDivElement>,
    timeSlot: Timeslot,
    dayIndex: number,
    timeIndex: number
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onMouseDown(e as any, timeSlot);
    }

    const getNext = (nextTimeIndex: number, nextDayIndex: number) => {
      const selector = `[data-time-index="${nextTimeIndex}"][data-day-index="${nextDayIndex}"]`;
      const nextEl = document.querySelector(selector) as HTMLElement | null;
      nextEl?.focus();
    };

    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        getNext(timeIndex, dayIndex + 1);
        break;
      case "ArrowLeft":
        e.preventDefault();
        getNext(timeIndex, dayIndex - 1);
        break;
      case "ArrowDown":
        e.preventDefault();
        getNext(timeIndex + 1, dayIndex);
        break;
      case "ArrowUp":
        e.preventDefault();
        getNext(timeIndex - 1, dayIndex);
        break;
    }
  };

  return (
    <div
      className={twMerge(
        "time-slot",
        "touch-pan-x",
        "h-6 cursor-pointer relative",
        "border-r border-t border-solid border-brand-300 transition-colors duration-100",
        isSelected &&
          matchingSlot?.duration &&
          matchingSlot.duration >= 2 &&
          "bg-brand-600 hover:bg-brand-500",
        isSelected &&
          matchingSlot?.duration &&
          matchingSlot.duration < 2 &&
          "bg-brand-400 hover:bg-brand-300",
        !isSelected && "hover:bg-brand-100",
        timeSlot.local.start.hour === END_HOUR - 1 &&
          timeSlot.local.start.minute === 30 &&
          "border-b",
        "focus:outline-none focus:z-20 focus:border focus:ring-2 focus:ring-offset-0 focus:ring-brand-600"
      )}
      style={{
        borderTopStyle: timeSlot.local.start.minute === 0 ? "solid" : "dashed",
      }}
      tabIndex={0}
      role="gridcell"
      aria-label={`Time slot: ${DAYS_FULL[dayIndex]}, ${moment()
        .hour(timeSlot.local.start.hour)
        .minute(timeSlot.local.start.minute)
        .format("h:mm A")}. ${isSelected ? "Selected." : "Not selected."}`}
      aria-roledescription="Time slot"
      data-slot-id={timeSlot.id.toString()}
      onMouseDown={(e) => onMouseDown(e, timeSlot)}
      onMouseOver={(e) => onMouseOver(e, timeSlot)}
      onTouchStart={(e) => onTouchStart(e, timeSlot)}
      onTouchMove={(e) => onTouchMove(e, timeSlot)}
      onTouchEnd={(e) => onTouchEnd(e, timeSlot)}
      onKeyDown={(e) => onKeyDown(e, timeSlot, dayIndex, timeIndex)}
      data-time-index={timeIndex}
      data-day-index={dayIndex}
    >
      {/* Duration badge shown only for first slot in contiguous block */}
      {isSelected && isFirstSlotInContiguousBlock && (
        <SchedulingAvailabilityTimeSlotBadge
          duration={matchingSlot?.duration}
        />
      )}
    </div>
  );
}

export default SchedulingAvailabilityTimeSlot;
